import React from 'react';
import { useLocation } from 'react-router-dom';
import { useI18n } from '../i18n/LanguageContext';
import { EmailNotConfiguredError, sendQuoteRequest } from '../services/quoteEmail';

function buildPrefillMessage(t, search) {
  const params = new URLSearchParams(search);
  const category = params.get('category') || '';
  const neck = params.get('neck') || '';
  const capacity = params.get('capacity') || '';

  const segments = (params.get('segments') || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const neckTypes = (params.get('neckTypes') || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const shapes = (params.get('shapes') || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const industries = (params.get('industries') || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const capFrom = params.get('capFrom') || '';
  const capTo = params.get('capTo') || '';

  const productId = params.get('productId') || '';
  const productTitle = params.get('productTitle') || '';
  const fillVolumeMl = params.get('fillVolumeMl') || '';
  const weightG = params.get('weightG') || '';
  const material = params.get('material') || '';
  const neckTypeLabel = params.get('neckType') || '';
  const shapeLabel = params.get('shape') || '';

  if (
    !category &&
    !neck &&
    !capacity &&
    !segments.length &&
    !neckTypes.length &&
    !shapes.length &&
    !industries.length &&
    !capFrom &&
    !capTo &&
    !productId &&
    !productTitle &&
    !fillVolumeMl &&
    !weightG &&
    !material &&
    !neckTypeLabel &&
    !shapeLabel
  )
    return '';

  const normalizedCategory = (() => {
    if (category === 'capace' || category === 'manere' || category === 'triggere') return 'capace-manere';
    if (category === 'galeti' || category === 'bidoane-hdpe') return 'galeti-bidoane';
    return category;
  })();

  const categoryTitle = (() => {
    if (normalizedCategory === 'sticle-pet') return t('products.items.petBottles').title;
    if (normalizedCategory === 'preforme') return t('products.items.preforms').title;
    if (normalizedCategory === 'capace-manere') return t('products.items.capsHandles').title;
    if (normalizedCategory === 'galeti-bidoane') return t('products.items.bucketsGallons').title;
    return normalizedCategory || category;
  })();

  const lines = [`${t('contact.prefill.request')} - ${categoryTitle}`];

  if (productTitle) {
    lines.push(`${t('products.pet.aboutTitle')}: ${productTitle}`);
  }
  if (segments.length) {
    const segLabels = segments.map((id) => t(`products.filters.options.segments.${id}`));
    lines.push(`${t('products.filters.marketSegment')}: ${segLabels.join(', ')}`);
  }
  if (neckTypes.length) {
    const neckLabels = neckTypes.map((id) => t(`products.filters.options.neckTypes.${id}`));
    lines.push(`${t('products.filters.neckType')}: ${neckLabels.join(', ')}`);
  }
  if (shapes.length) {
    const shapeLabels = shapes.map((id) => t(`products.filters.options.shapes.${id}`));
    lines.push(`${t('products.filters.shape')}: ${shapeLabels.join(', ')}`);
  }
  if (industries.length) {
    lines.push(`${t('products.filters.industry')}: ${industries.join(', ')}`);
  }
  if (capFrom || capTo) {
    const a = capFrom || '50';
    const b = capTo || '10000';
    lines.push(`${t('products.filters.capacity')}: ${a}–${b} ml`);
  }
  if (fillVolumeMl) lines.push(`${t('products.pet.specs.fillVolume')}: ${fillVolumeMl} ml`);
  if (weightG) lines.push(`${t('products.pet.specs.weight')}: ${weightG} g`);
  if (material) lines.push(`${t('products.pet.specs.material')}: ${material}`);
  if (neckTypeLabel) lines.push(`${t('products.filters.neckType')}: ${neckTypeLabel}`);
  if (shapeLabel) lines.push(`${t('products.filters.shape')}: ${shapeLabel}`);
  if (neck) lines.push(`${t('contact.prefill.neckType')}: ${neck}`);
  if (capacity) lines.push(`${t('contact.prefill.capacity')}: ${capacity}`);

  return `${lines.join('\n')}\n\n`;
}

export default function Contact() {
  const { t, lang } = useI18n();
  const location = useLocation();
  const toEmail = 'vanzari@youngerkarom.ro';
  const mapsEmbedSrc = 'https://www.google.com/maps?q=44.4509729,26.1471271&z=17&output=embed';

  const initialMessage = React.useMemo(
    () => buildPrefillMessage(t, location.search),
    [t, location.search]
  );

  const [form, setForm] = React.useState(() => ({
    name: '',
    company: '',
    phone: '',
    email: '',
    message: initialMessage,
  }));
  const [status, setStatus] = React.useState('idle'); // idle | sending | success | error
  const [statusDetail, setStatusDetail] = React.useState('');
  const [showMissingFieldErrors, setShowMissingFieldErrors] = React.useState(false);

  React.useEffect(() => {
    if (!initialMessage) return;
    setForm((f) => (f.message ? f : { ...f, message: initialMessage }));
  }, [initialMessage]);

  const isFormComplete = React.useMemo(() => {
    return [form.name, form.company, form.phone, form.email, form.message].every((v) => String(v || '').trim());
  }, [form]);

  const missingFieldKeys = React.useMemo(() => {
    const requiredFields = ['name', 'company', 'phone', 'email', 'message'];
    return requiredFields.filter((key) => !String(form[key] || '').trim());
  }, [form]);

  const missingFieldLabels = React.useMemo(
    () => missingFieldKeys.map((key) => t(`contact.form.${key}`)),
    [missingFieldKeys, t]
  );

  async function onSubmit(e) {
    e.preventDefault();

    if (missingFieldKeys.length > 0) {
      setShowMissingFieldErrors(true);
      setStatus('error');
      setStatusDetail(`${t('contact.status.requiredFieldsPrefix')} ${missingFieldLabels.join(', ')}.`);
      return;
    }

    setStatus('sending');
    setStatusDetail('');

    try {
      await sendQuoteRequest({
        name: form.name,
        company: form.company,
        phone: form.phone,
        email: form.email,
        message: form.message,
        lang,
      });

      setStatus('success');
  setShowMissingFieldErrors(false);
      setForm({ name: '', company: '', phone: '', email: '', message: '' });
    } catch (err) {
      setStatus('error');
      if (err instanceof EmailNotConfiguredError) {
        setStatusDetail(t('contact.status.notConfigured'));
      } else {
        setStatusDetail(t('contact.status.error'));
      }
    }
  }

  return (
    <section className="yk-page">
      <h1 className="yk-h1">{t('contact.title')}</h1>
      <p className="yk-lead">{t('contact.intro')}</p>

      <div className="yk-twoCol">
        <div className="yk-card">
          <h2 className="yk-h2">{t('contact.detailsTitle')}</h2>
          <p className="yk-text">{t('contact.detailsBody')}</p>
          <p className="yk-text" style={{ marginTop: 10 }}>
            <strong>{t('contact.addressLabel')}:</strong> {t('contact.addressValue')}
          </p>
          <p className="yk-text" style={{ marginTop: 10 }}>
            <strong>{t('contact.salesPhoneLabel')}:</strong>{' '}
            <a href="tel:+40722256339">+40 722 256 339</a>
          </p>
          <p className="yk-text" style={{ marginTop: 6 }}>
            <strong>{t('contact.secretariatPhoneLabel')}:</strong>{' '}
            <a href="tel:+40212525453">+40 21 252 54 53</a>
          </p>
          <p className="yk-text" style={{ marginTop: 10 }}>
            <strong>{t('contact.form.email')}:</strong>{' '}
            <a href={`mailto:${toEmail}`}>{toEmail}</a>
          </p>

          <div className="yk-mapBox" aria-label="Google Maps">
            <iframe
              className="yk-mapIframe"
              title="Younger Karom - Google Maps"
              src={mapsEmbedSrc}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>

        <form className="yk-card" onSubmit={onSubmit} noValidate>
          <h2 className="yk-h2">{t('contact.formTitle')}</h2>

          <label className="yk-label">
            {t('contact.form.name')}
            <input
              className={`yk-input${showMissingFieldErrors && missingFieldKeys.includes('name') ? ' yk-inputInvalid' : ''}`}
              name="name"
              autoComplete="name"
              required
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </label>

            <label className="yk-label">
              {t('contact.form.company')}
              <input
                className={`yk-input${showMissingFieldErrors && missingFieldKeys.includes('company') ? ' yk-inputInvalid' : ''}`}
                name="company"
                autoComplete="organization"
                required
                value={form.company}
                onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
              />
            </label>

            <label className="yk-label">
              {t('contact.form.phone')}
              <input
                className={`yk-input${showMissingFieldErrors && missingFieldKeys.includes('phone') ? ' yk-inputInvalid' : ''}`}
                type="tel"
                name="phone"
                autoComplete="tel"
                required
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              />
            </label>

          <label className="yk-label">
            {t('contact.form.email')}
            <input
              className={`yk-input${showMissingFieldErrors && missingFieldKeys.includes('email') ? ' yk-inputInvalid' : ''}`}
              type="email"
              name="email"
              autoComplete="email"
              required
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            />
          </label>

          <label className="yk-label">
            {t('contact.form.message')}
            <textarea
              className={`yk-textarea${showMissingFieldErrors && missingFieldKeys.includes('message') ? ' yk-inputInvalid' : ''}`}
              name="message"
              rows={5}
              required
              value={form.message}
              onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
            />
          </label>

          {status === 'success' ? (
            <p className="yk-status yk-statusOk">{t('contact.status.success')}</p>
          ) : null}
          {status === 'error' ? (
            <p className="yk-status yk-statusErr yk-statusErrBox">{statusDetail || t('contact.status.error')}</p>
          ) : null}
          {statusDetail && status !== 'error' ? <p className="yk-status">{statusDetail}</p> : null}

          <button className="yk-btn yk-btnPrimary" type="submit" disabled={status === 'sending'}>
            {status === 'sending' ? t('contact.form.sending') : t('contact.form.submit')}
          </button>
        </form>
      </div>
    </section>
  );
}
